// WaitingRoom — the online lobby's between-state: the room exists (config registered) but the match has
// NOT started. The host (roomState.host) shares the room code, watches friends join live (roomState over
// the table WS), tweaks the player count / ruleset, and presses 開始 to deal. Joiners just wait. Rendered
// by SoloMahjong while RemoteTable.phase === 'waiting'; the board takes over on the first snapshot (phase →
// 'playing'). After a gameOver the server resets the room here (rematch lobby) with finalScores retained —
// we show the final standings + a rematch (host start) / leave path. [[unified-play-surface]].
import { useState, useSyncExternalStore, type CSSProperties } from 'react';
import type { RuleSet, SeatList } from '@mj/protocol';
import type { RemoteTable } from '../play/remoteTable';
import { RULESET_PRESETS, buildPreset } from '../presets';
import { errJa } from '../panels/errJa';

// fit-always (no scroll): the card fills the available height and every section is sized so the whole
// lobby — incl. a 7-seat list + host config + 開始 — stays within one mobile viewport. The seat list is a
// read-only status grid (not tap targets) so its rows are compact; interactive controls keep ≥40px height.
const WRAP: CSSProperties = {
  height: '100%',
  padding: 'calc(var(--sp-3) + env(safe-area-inset-top)) var(--sp-3) calc(var(--sp-3) + env(safe-area-inset-bottom))',
  boxSizing: 'border-box',
  color: 'var(--text-hi)',
  fontFamily: 'var(--font-ui)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  overflow: 'hidden',
};
const CARD: CSSProperties = {
  width: 'min(560px, 100%)',
  maxHeight: '100%',
  boxSizing: 'border-box',
  background: 'var(--surface-card)',
  border: '1px solid var(--border-panel)',
  borderRadius: 'var(--r-md)',
  padding: 'var(--sp-3)',
  boxShadow: 'var(--shadow-2)',
  backdropFilter: 'var(--blur-panel)',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--sp-2)',
};
const CARD_TITLE: CSSProperties = { margin: 0, color: 'var(--gold-300)', fontSize: 15, fontWeight: 700 };
const CODE: CSSProperties = {
  fontSize: 'clamp(24px, 7vw, 40px)',
  fontWeight: 700,
  letterSpacing: '0.18em',
  color: 'var(--gold-100)',
  fontFamily: 'var(--font-display)',
};
// read-only status row (seat list / standings): compact, not a tap target.
const SEAT_ROW: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'var(--sp-2)',
  minHeight: 26,
  padding: '1px var(--sp-2)',
  borderTop: '1px solid rgba(255,255,255,0.06)',
};
// seat/standings list as a 2-column grid when there are many seats, so 7 rows never blow the height budget.
const listGrid = (n: number): CSSProperties => ({
  display: 'grid',
  gridTemplateColumns: n > 4 ? '1fr 1fr' : '1fr',
  columnGap: 'var(--sp-3)',
});
const FIELD: CSSProperties = {
  minHeight: 40, boxSizing: 'border-box',
  background: 'var(--surface-well)', color: 'var(--text-hi)',
  border: '1px solid var(--border-panel)', borderRadius: 'var(--r-sm)',
  padding: '6px 10px', fontSize: 14, fontFamily: 'inherit',
};
const ERR: CSSProperties = {
  color: 'var(--c-loss)', background: 'rgba(229,72,77,0.12)', border: '1px solid rgba(229,72,77,0.35)',
  borderRadius: 'var(--r-sm)', padding: 'var(--sp-2) var(--sp-3)', fontSize: 13, lineHeight: 1.5,
};

const OCCUPANT_JA: Record<SeatList[number]['occupant'], string> = {
  human: 'プレイヤー',
  bot: 'CPU',
  empty: '空席',
};

function ConnBadge({ status }: { status: 'connecting' | 'open' | 'closed' }) {
  const cfg = status === 'open'
    ? { dot: 'var(--c-win, #3fb950)', label: '接続中', color: 'var(--text-mid)' }
    : status === 'connecting'
      ? { dot: 'var(--gold-400, #e8c14a)', label: '接続中…', color: 'var(--text-mid)' }
      : { dot: 'var(--c-danger, #e5484d)', label: '再接続中…', color: 'var(--c-danger, #e5484d)' };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: cfg.color, whiteSpace: 'nowrap' }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: cfg.dot, flex: '0 0 auto' }} aria-hidden />
      {cfg.label}
    </span>
  );
}

export function WaitingRoom({ table, onLeave }: { table: RemoteTable; onLeave: () => void }) {
  // subscribe so the seat list / room code / host / players / connStatus / errors re-render as the server
  // pushes roomState. The snapshot folds every field the lobby renders so any change forces a re-render.
  useSyncExternalStore(
    (cb) => table.subscribe(cb),
    () => [
      table.connStatus, table.phase, table.roomCode, table.players, table.host,
      table.fatal, table.lastError, table.finalScores?.join(','),
      table.seats.map((s) => `${s.seat}${s.occupant}${s.displayName ?? ''}`).join('|'),
    ].join(';'),
  );
  const [copied, setCopied] = useState(false);
  const [pendingPreset, setPendingPreset] = useState<string | null>(null);

  const seats = table.seats;
  const code = table.roomCode;
  const isHost = table.isHost;
  const host = table.host;
  const filled = seats.filter((s) => s.occupant === 'human').length;
  const total = seats.length || table.players;
  const allHuman = seats.length > 0 && seats.every((s) => s.occupant === 'human');
  const hasEmpty = seats.some((s) => s.occupant === 'empty');
  // a player count below the highest occupied seat is illegal (server replies ILLEGAL_MOVE) — gate the select.
  const maxOccupied = seats.reduce((m, s) => (s.occupant !== 'empty' ? Math.max(m, s.seat + 1) : m), 1);
  const finalScores = table.finalScores; // non-null after a gameOver → show the standings + rematch path.
  const fatal = table.fatal;

  async function copyCode(): Promise<void> {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
    } catch {
      // クリップボード不可環境は無視 — コードは画面に表示済み。
    }
  }

  const onPlayers = (n: number): void => {
    // keep the ruleset's player count in step with the seat count so scoring/seat-wind tables stay valid.
    const rs = { ...table.ruleSet, players: n as RuleSet['players'] };
    table.updateConfig(n as RuleSet['players'], rs);
  };
  const applyPreset = (id: string): void => {
    const built = buildPreset(id);
    if (built) table.updateConfig(undefined, { ...built, players: table.players as RuleSet['players'] });
    setPendingPreset(null);
  };

  // ranked standings (desc by score) — used for the gameOver result table.
  const standings = finalScores
    ? finalScores.map((score, seat) => ({ seat, score })).sort((a, b) => b.score - a.score)
    : null;

  return (
    <main className="table-surface" style={WRAP}>
      <div style={CARD}>
        <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--sp-3)' }}>
          <h2 style={CARD_TITLE}>{standings ? '対局終了' : '待機ロビー'}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
            <ConnBadge status={table.connStatus} />
            <button type="button" className="btn-secondary" onClick={onLeave}>退出</button>
          </div>
        </header>

        {fatal && <div role="alert" style={ERR}>{errJa(fatal)} — 退出してロビーに戻ってください。</div>}
        {!fatal && table.lastError && <div role="alert" style={ERR}>{errJa(table.lastError)}</div>}

        {/* ── gameOver: final standings (rematch lobby) ── */}
        {standings && (
          <section>
            <div style={{ color: 'var(--text-low)', fontSize: 12, marginBottom: 'var(--sp-1)' }}>最終結果</div>
            <div style={listGrid(standings.length)}>
              {standings.map((row, i) => (
                <div key={row.seat} style={SEAT_ROW}>
                  <span style={{ color: 'var(--text-hi)', fontSize: 13 }}>
                    {i + 1}位 · 席 {row.seat + 1}
                    {row.seat === table.humanSeat && <span style={{ color: 'var(--gold-300)' }}>（あなた）</span>}
                  </span>
                  <span className="num" style={{ color: i === 0 ? 'var(--gold-200, #f4d97b)' : 'var(--text-mid)', fontSize: 14, fontWeight: 700 }}>
                    {row.score.toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          </section>
        )}

        {!standings && (
          <div>
            <div style={{ color: 'var(--text-mid)', fontSize: 14, lineHeight: 1.6, marginBottom: 'var(--sp-2)' }}>
              この部屋コードを相手に共有してください。
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)', flexWrap: 'wrap' }}>
              <code style={CODE}>{code || '—'}</code>
              <button type="button" className="btn-secondary" onClick={() => void copyCode()} disabled={!code}>
                {copied ? 'コピー済み ✓' : 'コピー'}
              </button>
            </div>
          </div>
        )}

        <section>
          <div style={{ color: 'var(--text-low)', fontSize: 12, marginBottom: 'var(--sp-1)' }}>
            参加者 {filled} / {total}
          </div>
          <div style={listGrid(seats.length)}>
            {seats.map((s) => (
              <div key={s.seat} style={SEAT_ROW}>
                <span style={{ color: 'var(--text-hi)', fontSize: 13, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                  席 {s.seat + 1}
                  {host === s.seat && <span title="ホスト" aria-label="ホスト" style={{ color: 'var(--gold-300)' }}>👑</span>}
                  {s.seat === table.humanSeat && <span style={{ color: 'var(--gold-300)' }}>（あなた）</span>}
                </span>
                <span style={{ color: s.occupant === 'empty' ? 'var(--text-low)' : 'var(--text-mid)', fontSize: 12 }}>
                  {OCCUPANT_JA[s.occupant]}
                </span>
              </div>
            ))}
          </div>
        </section>

        {/* ── host-only config (waiting only; server rejects non-host with NOT_HOST) ── */}
        {isHost && !fatal ? (
          <section style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
            <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--sp-3)' }}>
              <span style={{ color: 'var(--text-mid)', fontSize: 13 }}>人数</span>
              <select
                value={table.players}
                onChange={(e) => onPlayers(Number(e.currentTarget.value))}
                style={{ ...FIELD, width: 120 }}
              >
                {[1, 2, 3, 4, 5, 6].map((n) => (
                  <option key={n} value={n} disabled={n < maxOccupied}>{n}人</option>
                ))}
              </select>
            </label>

            <div>
              <div style={{ color: 'var(--text-mid)', fontSize: 13, marginBottom: 'var(--sp-2)' }}>ルール</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-2)' }}>
                {RULESET_PRESETS.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    aria-pressed={pendingPreset === p.id}
                    onClick={() => setPendingPreset((v) => (v === p.id ? null : p.id))}
                    style={{
                      flex: '1 1 auto', textAlign: 'left', padding: '8px 10px', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit',
                      background: 'var(--surface-well)', color: 'var(--text-hi)', fontSize: 12,
                      border: `1px solid ${pendingPreset === p.id ? 'var(--gold-400)' : 'var(--border-panel)'}`,
                    }}
                  >
                    {p.name.ja}
                  </button>
                ))}
              </div>
              {pendingPreset !== null && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', fontSize: 12, color: 'var(--text-mid)', marginTop: 'var(--sp-2)', flexWrap: 'wrap' }}>
                  このルールに変更します
                  <button type="button" className="btn-primary" style={{ minHeight: 40 }} onClick={() => applyPreset(pendingPreset)}>適用</button>
                  <button type="button" className="btn-secondary" style={{ minHeight: 40 }} onClick={() => setPendingPreset(null)}>キャンセル</button>
                </div>
              )}
            </div>

            {allHuman ? (
              <button type="button" className="btn-primary" onClick={() => table.start(false)}>
                {standings ? 'もう一局' : '開始'}
              </button>
            ) : (
              <button type="button" className="btn-primary" onClick={() => table.start(true)} disabled={!hasEmpty}>
                {standings ? 'CPUで埋めてもう一局' : 'CPUで埋めて開始'}
              </button>
            )}
          </section>
        ) : !fatal ? (
          <div style={{ color: 'var(--text-mid)', fontSize: 14, textAlign: 'center' }}>
            {standings ? 'ホストの再戦待ち…' : 'ホストの設定・開始待ち…'}
          </div>
        ) : null}
      </div>
    </main>
  );
}
